import React from 'react';
import { Building, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { IconDropdownMenu } from '@/components/ui/IconDropdownMenu';
import { formatPhone } from '@/utils/formatPhone';
import { LOSS_CATEGORIES, isUnlistedLossCategory } from '@/constants/lossCategories';

interface JobInsuranceInfoProps {
    lossDate: string;
    setLossDate: (val: string) => void;
    lossCategory: string;
    setLossCategory: (val: string) => void;
    carrier: string;
    setCarrier: (val: string) => void;
    claimNumber: string;
    setClaimNumber: (val: string) => void;
    policyNumber: string;
    setPolicyNumber: (val: string) => void;
    adjusterName: string;
    setAdjusterName: (val: string) => void;
    adjusterEmail: string;
    setAdjusterEmail: (val: string) => void;
    adjusterPhone: string;
    setAdjusterPhone: (val: string) => void;
    iicrcCategory: number;
    setIicrcCategory: (val: number) => void;
    iicrcClass: number;
    setIicrcClass: (val: number) => void;
}

export const JobInsuranceInfo: React.FC<JobInsuranceInfoProps> = ({
    lossDate, setLossDate,
    lossCategory, setLossCategory,
    carrier, setCarrier,
    claimNumber, setClaimNumber,
    policyNumber, setPolicyNumber,
    adjusterName, setAdjusterName,
    adjusterEmail, setAdjusterEmail,
    adjusterPhone, setAdjusterPhone,
    iicrcCategory, setIicrcCategory,
    iicrcClass, setIicrcClass,
}) => {
    const categoryLabel =
        iicrcCategory === 1 ? 'Clean Water'
            : iicrcCategory === 2 ? 'Gray Water'
                : iicrcCategory === 3 ? 'Black Water'
                    : 'Not set';
    const classLabel =
        iicrcClass === 1 ? 'Minor / Partial'
            : iicrcClass === 2 ? 'Significant / Full'
                : iicrcClass === 3 ? 'Extensive / Saturated'
                    : iicrcClass === 4 ? 'Specialty Drying'
                        : 'Not set';

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 text-status-mitigation mb-4">
                <Building size={18} />
                <h3 className="text-sm font-black uppercase tracking-widest">Loss & Insurance</h3>
            </div>

            <div className="space-y-1">
                <label className="text-micro font-bold text-text-muted uppercase">Date of Loss</label>
                <input
                    type="date"
                    value={lossDate}
                    onChange={(e) => setLossDate(e.target.value)}
                    className="input-field"
                />
            </div>

            <div className="space-y-1">
                <label className="text-micro font-bold text-text-muted uppercase">Loss Type</label>
                <select
                    value={lossCategory}
                    onChange={(e) => setLossCategory(e.target.value)}
                    className="input-field appearance-none"
                >
                    <option value="" className="bg-bg-tertiary">Select Category...</option>
                    {isUnlistedLossCategory(lossCategory) && (
                        <option value={lossCategory} className="bg-bg-tertiary">{lossCategory}</option>
                    )}
                    {LOSS_CATEGORIES.map((c) => (
                        <option key={c} value={c} className="bg-bg-tertiary">{c}</option>
                    ))}
                </select>
            </div>

            {/* IICRC Classification */}
            <div className="space-y-2 bg-surface-hover p-4 rounded-xl border border-divider-subtle">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert size={14} className="text-red-500" />
                    <label className="text-micro font-bold text-text-muted uppercase">IICRC Classification</label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="relative iicrc-category-tile rounded-lg p-2.5">
                        <div className="absolute top-2 right-2">
                            <IconDropdownMenu
                                items={[
                                    { id: '0', label: 'Not Set' },
                                    { id: '1', label: '1 — Clean Water' },
                                    { id: '2', label: '2 — Gray Water' },
                                    { id: '3', label: '3 — Black Water' }
                                ]}
                                onSelect={(id) => setIicrcCategory(Number(id))}
                                menuTitle="Select Category"
                                direction="down"
                                triggerContent={<SlidersHorizontal size={14} />}
                                buttonClassName="h-7 w-7"
                                showButtonBorder={false}
                                iconOnly
                            />
                        </div>
                        <div className="text-micro uppercase text-text-muted font-bold">Category</div>
                        <div className="text-lg font-black iicrc-tile-value mt-1 leading-none text-center">
                            {iicrcCategory > 0 ? iicrcCategory : '-'}
                        </div>
                        <div className="text-micro iicrc-tile-subtext text-center mt-1 truncate font-semibold">{categoryLabel}</div>
                    </div>
                    <div className="relative iicrc-class-tile rounded-lg p-2.5">
                        <div className="absolute top-2 right-2">
                            <IconDropdownMenu
                                items={[
                                    { id: '0', label: 'Not Set' },
                                    { id: '1', label: '1 — Minor / Partial' },
                                    { id: '2', label: '2 — Significant / Full' },
                                    { id: '3', label: '3 — Extensive / Saturated' },
                                    { id: '4', label: '4 — Specialty Drying' }
                                ]}
                                onSelect={(id) => setIicrcClass(Number(id))}
                                menuTitle="Select Class"
                                direction="down"
                                triggerContent={<SlidersHorizontal size={14} />}
                                buttonClassName="h-7 w-7"
                                showButtonBorder={false}
                                iconOnly
                            />
                        </div>
                        <div className="text-micro uppercase text-text-muted font-bold">Class</div>
                        <div className="text-lg font-black iicrc-tile-value mt-1 leading-none text-center">
                            {iicrcClass > 0 ? iicrcClass : '-'}
                        </div>
                        <div className="text-micro iicrc-tile-subtext text-center mt-1 truncate font-semibold">{classLabel}</div>
                    </div>
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-micro font-bold text-text-muted uppercase">Insurance Carrier or Self Pay</label>
                <input
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    className="input-field"
                    placeholder="e.g. State Farm or Self Pay"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <label className="text-micro font-bold text-text-muted uppercase">Claim Number</label>
                    <input
                        value={claimNumber}
                        onChange={(e) => setClaimNumber(e.target.value)}
                        className="input-field"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-micro font-bold text-text-muted uppercase">Policy Number</label>
                    <input
                        value={policyNumber}
                        onChange={(e) => setPolicyNumber(e.target.value)}
                        className="input-field"
                    />
                </div>
            </div>

            <div className="space-y-2 bg-surface-hover p-4 rounded-xl border border-divider-subtle">
                <label className="text-micro font-bold text-text-muted uppercase">Insurance Adjuster</label>
                <div className="flex flex-col gap-2">
                    <input
                        value={adjusterName}
                        onChange={(e) => setAdjusterName(e.target.value)}
                        className="input-field"
                        placeholder="Name (e.g. John Doe)"
                    />
                    <input
                        type="email"
                        value={adjusterEmail}
                        onChange={(e) => setAdjusterEmail(e.target.value)}
                        className="input-field"
                        placeholder="Email (e.g. john@insurance.com)"
                    />
                    <input
                        type="tel"
                        value={adjusterPhone}
                        onChange={(e) => setAdjusterPhone(formatPhone(e.target.value))}
                        className="input-field"
                        placeholder="(555) 555-5555"
                    />
                </div>
            </div>

        </div>
    );
};
